import React, { useState } from 'react'
import { motion } from 'framer-motion'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.3 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 20 },
  },
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    message: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch('https://devspots-mail-sender.vercel.app/sendmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error('Failed to send message.')
      }

      setIsSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <motion.div
        id="contact"
        className="max-w-7xl mx-auto p-4 sm:p-6 md:p-8 lg:p-10 gap-y-10 mt-2 sm:mt-4 md:mt-8 lg:mt-10 flex flex-col justify-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="text-center" variants={itemVariants}>
          <h1 className="text-4xl font-medium mb-7 text-[#000B27]">
            <span className="text-primaryColor">Thank you!</span> Your message has been sent.
          </h1>
          <p className="text-xl text-pretty">
            We'll get back to you as soon as possible.
          </p>
        </motion.div>
      </motion.div>
    )
  }

  return (
    <motion.div
      id="contact"
      className="max-w-7xl mx-auto p-4 sm:p-6 md:p-8 lg:p-10 gap-y-10 mt-2 sm:mt-4 md:mt-8 lg:mt-10 flex flex-col justify-center"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <motion.div
        className="grid sm:grid-cols-2 grid-cols-1 gap-10 mb-14"
        variants={containerVariants}
      >
        <motion.div className="flex flex-col" variants={itemVariants}>
          <h1 className="text-4xl font-medium mb-7 text-[#000B27]">
            <span className="text-primaryColor">Contact</span> Us
          </h1>
          <p className="text-xl text-pretty">
            We love making new connections. So, if you just want to say Hi,
            collaborate or have a project in mind, we're happy to hear from you!
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="bg-primaryColo bg-gradient-to-tr from-[#ED8B29] to-[#FFD23F] rounded-3xl px-8 py-9 flex flex-col gap-4"
          variants={itemVariants}
        >
          <input
            type="text"
            name="name"
            placeholder="Your name*"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full bg-[#ffe0b2] py-2 px-4 rounded-full placeholder:text-[#000B27]"
          />

          <input
            type="email"
            name="email"
            placeholder="Your email*"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full bg-[#ffe0b2] py-2 px-4 rounded-full placeholder:text-[#000B27]"
          />

          <input
            type="text"
            name="website"
            placeholder="Your website (if you have one)"
            value={formData.website}
            onChange={handleChange}
            className="w-full bg-[#ffe0b2] py-2 px-4 rounded-full placeholder:text-[#000B27]"
          />

          <textarea
            name="message"
            placeholder="Tell us a bit about your project*"
            required
            value={formData.message}
            onChange={handleChange}
            className="w-full bg-[#ffe0b2] py-2 px-4 rounded-xl placeholder:text-[#000B27] resize-none h-32"
          />

          <div className="flex items-center gap-2">
            <input type="checkbox" id="privacy" required className="cursor-pointer" />
            <label htmlFor="privacy" className="text-sm text-[#000B27]">
              I agree to the <span className="underline cursor-pointer">privacy policy</span>
            </label>
          </div>

          {error && (
            <p className="text-red-700 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#000B27] rounded-full py-2 px-14 text-white w-fit mt-2 hover:shadow-xl"
          >
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
        </motion.form>
      </motion.div>
    </motion.div>
  )
}

export default Contact
